<template>
	<div class="qromo-report-container">
		<div class="date-picker-container" :class="{'mb-20': !userStore?.dailyReportTimeSlots?.length}">
			<div class="date-container">
				<DatePicker ref="date-picker" v-model="date" mode="date" is24hr color="teal" :max-date='new Date()' :min-date="minDate" :is-dark="userStore.darkMode">
					<template #default="{ togglePopover }">
						<AppTextInput class="text-input" @click="togglePopover()" :value="formattedDate" readonly />
					</template>
				</DatePicker>
				<AppLoader v-if="loadingResults" :size="20" />
			</div>
			<div class="buttons-container">
				<AppButtonIcon icon-name="icon-filters" prevent-change @click-icon="stationsFilter = true" :active="filterStationActive" />
				<AppButtonIcon icon-name="icon-pos-white" prevent-change @click-icon="posDetails = true" v-if="terminalArray.length > 0" />
				<AppButtonIcon icon-name="icon-download-white" prevent-change @click-icon="exportTypePopup" />
				<AppButtonIcon v-if="showPrintButton" icon-name="icon-print-order" prevent-change @click-icon="sendPrintReportCommand" :loading="loadingPrinter" />
			</div>
		</div>
		<div class="row">
			<TimesSlotsSlider v-show="userStore?.dailyReportTimeSlots?.length" @active-time="changeTimeSlot"/>
		</div>
		<AppCard :title="getString('total_summary')" class="mb-20">
			<template #body>
				<div class="container-mini-cards-total">
					<div class="container-mini-card">
						<UserInitialCardInfo
							@click="paymentsBreakdown = currentResults.totalBreakdown"
							class="dark-blue"
							blue :title="getString('total')"
							:value="currentResults.total"
							:last-period-value="currentResults.totalPrevious"
							:symbol="currencyStore.currCurrency.symbol"
							:with-cents="true"
							:no-currency="false" />
					</div>
					<div class="container-mini-card">
						<UserInitialCardInfo
							blue :title="getString('total_electronic')"
							:value="currentResults.totalCards"
							:last-period-value="currentResults.totalCardsPrevious"
							:symbol="currencyStore.currCurrency.symbol"
							:with-cents="true"
							:no-currency="false" />
					</div>
					<div class="container-mini-card">
						<UserInitialCardInfo
							blue :title="getString('total_cash')"
							:value="currentResults.totalCash"
							:last-period-value="currentResults.totalCashPrevious"
							:symbol="currencyStore.currCurrency.symbol"
							:with-cents="true"
							:no-currency="false" />
					</div>
					<div class="container-mini-card">
						<UserInitialCardInfo
							blue :title="getString('total_other')"
							:value="currentResults.totalOther"
							:last-period-value="currentResults.totalOtherPrevious"
							:symbol="currencyStore.currCurrency.symbol"
							:with-cents="true"
							:no-currency="false" />
					</div>
					<div class="container-mini-card">
						<UserInitialCardInfo
							blue :title="getString('covers')"
							:value="currentResults.coverNum"
							:last-period-value="currentResults.coverNumPrevious"
							no-currency />
					</div>
					<div class="container-mini-card">
						<UserInitialCardInfo
							blue :title="getString('total_discounts')"
							:value="currentResults.discountTotal"
							:last-period-value="currentResults.discountTotalPrevious"
							:symbol="currencyStore.currCurrency.symbol"
							:with-cents="true"
							:no-currency="false" />
					</div>
					<div class="container-mini-card">
						<UserInitialCardInfo
							blue :title="getString('canceled_orders')"
							:value="currentResults.cancelTotal"
							:last-period-value="currentResults.cancelTotalPrevious"
							:with-cents="true"
							:no-currency="false" />
					</div>
					<div class="container-mini-card">
						<UserInitialCardInfo
							blue :title="getString('average_receipt')"
							:value="currentResults.averageReceipt"
							:last-period-value="currentResults.averageReceiptPrevious"
							:symbol="currencyStore.currCurrency.symbol"
							:with-cents="true"
							:no-currency="false" />
					</div>
				</div>
			</template>
		</AppCard>
		<QromoReportFiscalization v-if="fiscalVisible" :current-results="currentResults" @open-breakdown="v => paymentsBreakdown = v"  />
		<QromoReportMethods v-if="methodsVisible" :current-results="currentResults" @open-breakdown="v => paymentsBreakdown = v" />
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="posDetails" @toggling-popup="closeBottomSheet">
			<ModalHeader :title="getString('pos_details')" />
			<div class="terminals-container">
				<div class="terminal-container" v-for="terminal in terminalArray" :key="terminal.terminal_id">
					<div class="terminal-header">
						<div class="terminal-name">{{ terminal.name }}</div>
						<div class="transactions">{{ terminal.transactions > 1 ? getString("transactions_x", [terminal.transactions]) : getString("transaction_x") }}</div>
						<div class="refund-part" v-if="terminal.refunds > 0">{{ getString(terminal.refunds === 1 ? "terminal_refund" : "terminal_refunds", [terminal.refunds, currencyStore.readableCurrencyValue(terminal.refundsTotal, false)]) }}</div>
					</div>
					<div class="terminal-data">
						<div class="increment" :class="{negative: terminal.increment && parseInt(terminal.increment) < 0}" v-show="terminal.increment">{{ terminal.increment }}</div>
						<div class="value">{{ currencyStore.readableCurrencyValue(terminal.totalFormatted, false) }}</div>
					</div>
				</div>
			</div>
		</AppBottomSheet>
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="paymentsBreakdown !== null" @toggling-popup="closeBottomSheetBreakdown">
			<QromoReportBreakdown v-if="paymentsBreakdown" :breakdown="paymentsBreakdown" />
		</AppBottomSheet>
		<ExecutedByFilter :value="stationFilterValue" :show-bottom-sheet="stationsFilter"
						  @toggling-popup="closeBottomSheetStations" @filter-value-selected="applyExecutedByFilter" no-filter-type />
		<AppPopup
			ref="export-popup"
			:title="getString('export_type_title')"
			:buttons="exportButtons"/>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useScriptStore} from "@/shared/stores";
import UserInitialCardInfo from "@/user/components/analytics/UserInitialCardInfo.vue";
import {useAnalyticsStore, usePrintersStore, useUserStore} from "@/console-new/stores";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {formatDateOnly, formatDateTimeWithSeconds, formatDateYYMMDD} from "@/shared/helpers/dates";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import API from "@/shared/helpers/API";
import AppLoader from "@/shared/components/AppLoader.vue";
import {printReport} from "@/console-new/helpers/escpos";
import {getNative, isAndroid} from "@/shared/helpers/webapp";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import QromoReportFiscalization from "@/console-new/components/shared/QromoReportFiscalization.vue";
import QromoReportMethods from "@/console-new/components/shared/QromoReportMethods.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import ExecutedByFilter from "@/console-new/components/analytics/ExecutedByFilter.vue";
import QromoReportBreakdown from "@/console-new/components/shared/QromoReportBreakdown.vue";
import TimesSlotsSlider from "@/console-new/components/shared/TimesSlotsSlider.vue";
import AppPopup from "@/shared/components/AppPopup.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const analyticsStore = useAnalyticsStore();
		const printersStore = usePrintersStore();
		const notificationStore = useNotificationStore();
		const currencyStore = useCurrencyStore();
		const scriptStore = useScriptStore();
		return {
			getString: languageStore.getString,
			userStore,
			analyticsStore,
			printersStore,
			currencyStore,
			notificationStore,
			scriptStore
		}
	},
	name: "QromoReport",
	components: {
		TimesSlotsSlider,
		QromoReportBreakdown,
		ExecutedByFilter,
		AppChecklist,
		QromoReportFiscalization, QromoReportMethods,
		AppDropdown, AppChevronIcon, ModalHeader, AppLoader, AppButtonIcon, AppTextInput, UserInitialCardInfo, AppCard, AppBottomSheet,
		AppPopup
	},
	mixins: [WindowWidth],
	data() {
		return {
			date: new Date(),
			orders: [],
			ordersTerminals: [],
			paymentsBreakdown: null,
			posDetails: false,
			loadingResults: false,
			loadingPrinter: false,
			stationsFilter: false,
			selectedTimeSlot: null,
			stationFilterValue: {
				userToFilterBy: true,
				typeOfUserToFilterBy: 0,
				listOfUserToFilterBy: {},
				onlineClient: true,
			},
			exportButtons: [
				{
					text: this.getString("export_type_csv"),
					callback: () => {
						this.exportReport("csv");
						this.$refs['export-popup'].toggleStatus(false);
					}
				},
				{
					text: this.getString("export_type_excel"),
					callback: () => {
						this.exportReport("xlsx");
						this.$refs['export-popup'].toggleStatus(false);
					}
				}
			]
		}
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		showPrintButton() {
			return (this.printersStore.businessPrinters && this.printersStore.businessPrinters.length > 0);
		},
		minDate() {
			if(this.userStore.currPermissions.includes("analytics")) {
				return null;
			} else {
				let threeDaysAgo = new Date();
				threeDaysAgo.setDate(threeDaysAgo.getDate() - 2);
				return threeDaysAgo;
			}
		},
		modes() {
			let modes = [];
			if(this.userStore.currPlans.counter) {
				modes.push({
					id: 'counter',
					name: this.getString('counter_sale')
				});
			}
			if(this.userStore.currPlans.order_pay || this.userStore.currPlans.pay_bill) {
				modes.push({
					id: 'qr',
					name: this.getString('qr_code_tables')
				});
			}
			if(this.userStore.currPlans.delivery) {
				modes.push({
					id: 'delivery',
					name: this.getString('delivery')
				});
			}
			if(this.userStore.currPlans.takeaway) {
				modes.push({
					id: 'takeaway',
					name: this.getString('takeaway')
				});
			}
			return modes;
		},
		formattedDate() {
			return formatDateYYMMDD(this.date || new Date());
		},
		dayShift() {
			return this.userStore && this.userStore.currBusiness && this.userStore.currBusiness.day_shift ? this.userStore.currBusiness.day_shift : '03:00:00';
		},
		startDate() {
			if(this.selectedTimeSlot) {
				let currDate = new Date(this.date || new Date());
				currDate.setHours(Number(this.selectedTimeSlot.hour_start || 0), Number(this.selectedTimeSlot.minute_start || 0), 0, 0);
				return currDate;
			}
			return this.getLastShiftDatetime(this.date || new Date());
		},
		endDate() {
			if(this.selectedTimeSlot) {
				let isNextDay = this.selectedTimeSlot.hour_end < this.selectedTimeSlot.hour_start || (this.selectedTimeSlot.hour_end === this.selectedTimeSlot.hour_start && this.selectedTimeSlot.minute_end < this.selectedTimeSlot.minute_start);
				let currDate = new Date(this.date || new Date());
				if(isNextDay) {
					currDate.setDate(currDate.getDate() + 1);
				}
				currDate.setHours(Number(this.selectedTimeSlot.hour_end || 0), Number(this.selectedTimeSlot.minute_end || 0), 0, 0);
				return currDate;
			}
			let endDate = new Date(this.startDate);
			endDate.setDate(endDate.getDate() + 1);
			return endDate;
		},
		previousStartDate() {
			let startDate = new Date(this.startDate);
			startDate.setDate(startDate.getDate() - 1);
			return startDate;
		},
		currentResults() {
			let results = {
				total: 0,
				totalCards: 0,
				totalCash: 0,
				totalOther: 0,
				totalBreakdown: {},

				coverNum: 0,
				coverTotal: 0,
				discountTotal: 0,
				cancelNum: 0,
				cancelTotal: 0,
				numOrders: 0,
				averageReceipt: 0,

				fiscalPrinter: 0,
				fiscalPrinterCards: 0,
				fiscalPrinterCash: 0,
				fiscalPrinterOther: 0,
				fiscalPrinterNum: 0,
				fiscalPrinterCanceledNum: 0,
				fiscalPrinterCanceledValue: 0,
				fiscalPrinterBreakdown: {},

				apiscontrino: 0,
				apiscontrinoCards: 0,
				apiscontrinoCash: 0,
				apiscontrinoOther: 0,
				apiscontrinoNum: 0,
				apiscontrinoBreakdown: {},

				apifattura: 0,
				apifatturaCards: 0,
				apifatturaCash: 0,
				apifatturaOther: 0,
				apifatturaNum: 0,
				apifatturaBreakdown: {},

				modeCounter: 0,
				modeCounterCards: 0,
				modeCounterCash: 0,
				modeCounterOther: 0,
				modeCounterBreakdown: {},

				modeQrStaff: 0,
				modeQrStaffCards: 0,
				modeQrStaffCash: 0,
				modeQrStaffOther: 0,
				modeQrStaffBreakdown: {},

				modeQrOnline: 0,
				modeQrOnlineCards: 0,
				modeQrOnlineCash: 0,
				modeQrOnlineOther: 0,
				modeQrOnlineBreakdown: {},

				modeDeliveryStaff: 0,
				modeDeliveryStaffCards: 0,
				modeDeliveryStaffCash: 0,
				modeDeliveryStaffOther: 0,
				modeDeliveryStaffBreakdown: {},

				modeDeliveryOnline: 0,
				modeDeliveryOnlineCards: 0,
				modeDeliveryOnlineCash: 0,
				modeDeliveryOnlineOther: 0,
				modeDeliveryOnlineBreakdown: {},

				modeTakeawayStaff: 0,
				modeTakeawayStaffCards: 0,
				modeTakeawayStaffCash: 0,
				modeTakeawayStaffOther: 0,
				modeTakeawayStaffBreakdown: {},

				modeTakeawayOnline: 0,
				modeTakeawayOnlineCards: 0,
				modeTakeawayOnlineCash: 0,
				modeTakeawayOnlineOther: 0,
				modeTakeawayOnlineBreakdown: {},

			};
			let resultsPrevious = {
				total: 0,
				totalCards: 0,
				totalCash: 0,
				totalOther: 0,
				totalBreakdown: {},

				coverNum: 0,
				coverTotal: 0,
				discountTotal: 0,
				cancelNum: 0,
				cancelTotal: 0,
				numOrders: 0,
				averageReceipt: 0,

				fiscalPrinter: 0,
				fiscalPrinterCards: 0,
				fiscalPrinterCash: 0,
				fiscalPrinterOther: 0,
				fiscalPrinterCanceledNum: 0,
				fiscalPrinterCanceledValue: 0,
				fiscalPrinterBreakdown: {},

				apiscontrino: 0,
				apiscontrinoCards: 0,
				apiscontrinoCash: 0,
				apiscontrinoOther: 0,
				apiscontrinoBreakdown: {},

				apifattura: 0,
				apifatturaCards: 0,
				apifatturaCash: 0,
				apifatturaOther: 0,
				apifatturaBreakdown: {},

				modeCounter: 0,
				modeCounterCards: 0,
				modeCounterCash: 0,
				modeCounterOther: 0,
				modeCounterBreakdown: {},

				modeQrStaff: 0,
				modeQrStaffCards: 0,
				modeQrStaffCash: 0,
				modeQrStaffOther: 0,
				modeQrStaffBreakdown: {},

				modeQrOnline: 0,
				modeQrOnlineCards: 0,
				modeQrOnlineCash: 0,
				modeQrOnlineOther: 0,
				modeQrOnlineBreakdown: {},

				modeDeliveryStaff: 0,
				modeDeliveryStaffCards: 0,
				modeDeliveryStaffCash: 0,
				modeDeliveryStaffOther: 0,
				modeDeliveryStaffBreakdown: {},

				modeDeliveryOnline: 0,
				modeDeliveryOnlineCards: 0,
				modeDeliveryOnlineCash: 0,
				modeDeliveryOnlineOther: 0,
				modeDeliveryOnlineBreakdown: {},

				modeTakeawayStaff: 0,
				modeTakeawayStaffCards: 0,
				modeTakeawayStaffCash: 0,
				modeTakeawayStaffOther: 0,
				modeTakeawayStaffBreakdown: {},

				modeTakeawayOnline: 0,
				modeTakeawayOnlineCards: 0,
				modeTakeawayOnlineCash: 0,
				modeTakeawayOnlineOther: 0,
				modeTakeawayOnlineBreakdown: {},

			};

			this.orders.forEach(order => {

				if(order.staff_user_id === null) {
					if(!this.stationFilterValue.onlineClient) {
						return;
					}
				} else {
					if(!this.stationFilterValue.userToFilterBy) {
						return;
					}
					if(this.stationFilterValue.listOfUserToFilterBy && !this.stationFilterValue.listOfUserToFilterBy[order.staff_user_id]) {
						return;
					}
				}

				let resToUse;
				if(order.date >= this.startDate && order.date < this.endDate) {
					resToUse = results;
				} else if(order.date >= this.previousStartDate && order.date < this.startDate) {
					resToUse = resultsPrevious;
                } else {
                    return;
				}

				let electronicTypes = ["stripe", "klarna", "satispay", "btc", "external"];

				//Fiscalization

				let fiscalKey = null;
				if(order.invoice_id) {
					fiscalKey = "apifattura";
					resToUse.apifatturaNum++;
				} else if(order.fiscal_receipt) {
					if(order.receipt_id) {
						fiscalKey = "apiscontrino";
						if(order.type !== "multi") {
							resToUse.apiscontrinoNum++;
						}
					} else if(order.epson_receipt_id) {
						fiscalKey = "fiscalPrinter";
						if(order.type !== "multi") {
							resToUse.fiscalPrinterNum++;
							if(order.cancelReceipt) {
								resToUse.fiscalPrinterCanceledNum++;
								resToUse.fiscalPrinterCanceledValue += order.totalFormatted;
							}
						}
					}
				}
				if(electronicTypes.includes(order.type)) {
					if(fiscalKey) {
						resToUse[fiscalKey+"Cards"] += order.totalFormatted;
						resToUse[fiscalKey] += order.totalFormatted;
						resToUse[fiscalKey+"Breakdown"][order.type] = resToUse[fiscalKey+"Breakdown"][order.type] ? resToUse[fiscalKey+"Breakdown"][order.type] + order.totalFormatted : order.totalFormatted;
					}
				} else if(order.type === "cash") {
					if(fiscalKey) {
						if(order.paid) {
							resToUse[fiscalKey+"Cash"] += order.totalFormatted;
							resToUse[fiscalKey+"Breakdown"][order.type] = resToUse[fiscalKey+"Breakdown"][order.type] ? resToUse[fiscalKey+"Breakdown"][order.type] + order.totalFormatted : order.totalFormatted;
						} else {
							resToUse[fiscalKey+"Other"] += order.totalFormatted;
							resToUse[fiscalKey+"Breakdown"]["not_paid"] = resToUse[fiscalKey+"Breakdown"]["not_paid"] ? resToUse[fiscalKey+"Breakdown"]["not_paid"] + order.totalFormatted : order.totalFormatted;
						}
						resToUse[fiscalKey] += order.totalFormatted;
					}
				} else if(order.type === "multi") {
					let amountCheck = order.totalFormatted;
					order.payments.forEach(payment => {

						let amountFull = this.currencyStore.dbToActualValue(payment.amount);

						if(this.shouldMultiPaymentBeFilteredOut(payment)) {
							amountCheck -= amountFull;
							return;
						}

						fiscalKey = null;
						if(payment.invoice_id) {
							fiscalKey = "apifattura";
							resToUse.apifatturaNum++;
						} else if(payment.receipt_id) {
							fiscalKey = "apiscontrino";
							resToUse.apiscontrinoNum++;
						} else if(payment.epson_receipt_id) {
							fiscalKey = "fiscalPrinter";
							resToUse.fiscalPrinterNum++;
							if(payment.cancelReceipt) {
								resToUse.fiscalPrinterCanceledNum++;
								resToUse.fiscalPrinterCanceledValue += amountFull;
							}
						}

						if(fiscalKey) {
							resToUse[fiscalKey] += amountFull;
							resToUse[fiscalKey+"Breakdown"][payment.type] = resToUse[fiscalKey+"Breakdown"][payment.type] ? resToUse[fiscalKey+"Breakdown"][payment.type] + amountFull : amountFull;
							if(electronicTypes.includes(payment.type)) {
								resToUse[fiscalKey+"Cards"] += amountFull;
							} else if(payment.type === "cash") {
								resToUse[fiscalKey+"Cash"] += amountFull;
							} else {
								resToUse[fiscalKey+"Other"] += amountFull;
							}
						}

						amountCheck -= amountFull;
					});
					if(amountCheck > 0.1) {
						if(fiscalKey) {
							resToUse[fiscalKey+"Other"] += amountCheck;
							resToUse[fiscalKey] += amountCheck;
							resToUse[fiscalKey+"Breakdown"]["not_paid"] = resToUse[fiscalKey+"Breakdown"]["not_paid"] ? resToUse[fiscalKey+"Breakdown"]["not_paid"] + amountCheck : amountCheck;
						}
					}
				} else {
					if(fiscalKey) {
						resToUse[fiscalKey+"Other"] += order.totalFormatted;
						resToUse[fiscalKey] += order.totalFormatted;
						resToUse[fiscalKey+"Breakdown"][order.type] = resToUse[fiscalKey+"Breakdown"][order.type] ? resToUse[fiscalKey+"Breakdown"][order.type] + order.totalFormatted : order.totalFormatted;
					}
				}


				//General

				if(order.status === "canceled") {
					resToUse.cancelNum++;
					resToUse.cancelTotal += order.totalFormatted;
				} else {
					resToUse.numOrders++;
					resToUse.coverNum += order.cover_charge_num;
					resToUse.coverTotal += this.currencyStore.dbToActualValue(order.coverChargeTotal);

					resToUse.total += order.totalFormatted;
					if(electronicTypes.includes(order.type)) {
						resToUse.totalCards += order.totalFormatted;
						resToUse.totalBreakdown[order.type] = resToUse.totalBreakdown[order.type] ? resToUse.totalBreakdown[order.type] + order.totalFormatted : order.totalFormatted;
					} else if(order.type === "cash") {
						if(order.paid) {
							resToUse.totalCash += order.totalFormatted;
							resToUse.totalBreakdown[order.type] = resToUse.totalBreakdown[order.type] ? resToUse.totalBreakdown[order.type] + order.totalFormatted : order.totalFormatted;
						} else {
							resToUse.totalOther += order.totalFormatted;
							resToUse.totalBreakdown["not_paid"] = resToUse.totalBreakdown["not_paid"] ? resToUse.totalBreakdown["not_paid"] + order.totalFormatted : order.totalFormatted;
						}
					} else if(order.type === "multi") {
						let amountCheck = order.totalFormatted;
						order.payments.forEach(payment => {

							let amountFull = this.currencyStore.dbToActualValue(payment.amount);

							if(this.shouldMultiPaymentBeFilteredOut(payment)) {
								amountCheck -= amountFull;
								return;
							}

							resToUse.totalBreakdown[payment.type] = resToUse.totalBreakdown[payment.type] ? resToUse.totalBreakdown[payment.type] + amountFull : amountFull;
							if(electronicTypes.includes(payment.type)) {
								resToUse.totalCards += amountFull;
							} else if(payment.type === "cash") {
								resToUse.totalCash += amountFull;
							} else {
								resToUse.totalOther += amountFull;
							}
							amountCheck -= amountFull;
						});
						if(amountCheck > 0.1) {
							resToUse.totalOther += amountCheck;
							resToUse.totalBreakdown["not_paid"] = resToUse.totalBreakdown["not_paid"] ? resToUse.totalBreakdown["not_paid"] + amountCheck : amountCheck;
						}
					} else {
						resToUse.totalOther += order.totalFormatted;
						resToUse.totalBreakdown[order.type] = resToUse.totalBreakdown[order.type] ? resToUse.totalBreakdown[order.type] + order.totalFormatted : order.totalFormatted;
					}

					//Modes

					let modeKey = null;
					if(order.order_mode === "counter") {
						modeKey = "Counter";
					} else if(order.order_mode === "qr") {
						if(order.staff_user_id !== null) {
							modeKey = "QrStaff";
						} else {
							modeKey = "QrOnline";
						}
					} else if(order.order_mode === "delivery") {
						if(order.staff_user_id !== null) {
							modeKey = "DeliveryStaff";
						} else {
							modeKey = "DeliveryOnline";
						}
					} else if(order.order_mode === "takeaway") {
						if(order.staff_user_id !== null) {
							modeKey = "TakeawayStaff";
						} else {
							modeKey = "TakeawayOnline";
						}
					}

					if(modeKey) {
						resToUse["mode"+modeKey] += order.totalFormatted;
						if(electronicTypes.includes(order.type)) {
							resToUse["mode"+modeKey+"Cards"] += order.totalFormatted;
							resToUse["mode"+modeKey+"Breakdown"][order.type] = resToUse["mode"+modeKey+"Breakdown"][order.type] ? resToUse["mode"+modeKey+"Breakdown"][order.type] + order.totalFormatted : order.totalFormatted;
						} else if(order.type === "cash") {
							if(order.paid) {
								resToUse["mode"+modeKey+"Cash"] += order.totalFormatted;
								resToUse["mode"+modeKey+"Breakdown"][order.type] = resToUse["mode"+modeKey+"Breakdown"][order.type] ? resToUse["mode"+modeKey+"Breakdown"][order.type] + order.totalFormatted : order.totalFormatted;
							} else {
								resToUse["mode"+modeKey+"Other"] += order.totalFormatted;
								resToUse["mode"+modeKey+"Breakdown"]["not_paid"] = resToUse["mode"+modeKey+"Breakdown"]["not_paid"] ? resToUse["mode"+modeKey+"Breakdown"]["not_paid"] + order.totalFormatted : order.totalFormatted;
							}
						} else if(order.type === "multi") {
							let amountCheck = order.totalFormatted;
							order.payments.forEach(payment => {
								let amountFull = this.currencyStore.dbToActualValue(payment.amount);

								if(this.shouldMultiPaymentBeFilteredOut(payment)) {
									amountCheck -= amountFull;
									return;
								}

								resToUse["mode"+modeKey+"Breakdown"][payment.type] = resToUse["mode"+modeKey+"Breakdown"][payment.type] ? resToUse["mode"+modeKey+"Breakdown"][payment.type] + amountFull : amountFull;
								if(electronicTypes.includes(payment.type)) {
									resToUse["mode"+modeKey+"Cards"] += amountFull;
								} else if(payment.type === "cash") {
									resToUse["mode"+modeKey+"Cash"] += amountFull;
								} else {
									resToUse["mode"+modeKey+"Other"] += amountFull;
								}
								amountCheck -= amountFull;
							});
							if(amountCheck > 0.1) {
								resToUse["mode"+modeKey+"Other"] += amountCheck;
								resToUse["mode"+modeKey+"Breakdown"]["not_paid"] = resToUse["mode"+modeKey+"Breakdown"]["not_paid"] ? resToUse["mode"+modeKey+"Breakdown"]["not_paid"] + amountCheck : amountCheck;
							}
						} else {
							resToUse["mode"+modeKey+"Other"] += order.totalFormatted;
							resToUse["mode"+modeKey+"Breakdown"][order.type] = resToUse["mode"+modeKey+"Breakdown"][order.type] ? resToUse["mode"+modeKey+"Breakdown"][order.type] + order.totalFormatted : order.totalFormatted;
						}
					}

					if(order.subtotal_delta < 0) {
						resToUse['discountTotal'] += this.currencyStore.dbToActualValue(Math.abs(order.subtotal_delta));
					}

				}

			});

			results.averageReceipt = results.numOrders ? results.total/results.numOrders : 0;
			resultsPrevious.averageReceipt = resultsPrevious.numOrders ? resultsPrevious.total/resultsPrevious.numOrders : 0;

			for(let key in resultsPrevious) {
                results[key + "Previous"] = resultsPrevious[key];
            }

			return results;
		},
		methodsVisible() {
			return this.currentResults.modeCounter > 0 || this.currentResults.modeQrStaff > 0 || this.currentResults.modeQrOnline > 0 || this.currentResults.modeDeliveryStaff > 0 || this.currentResults.modeDeliveryOnline > 0 || this.currentResults.modeTakeawayStaff > 0 || this.currentResults.modeTakeawayOnline > 0 ||
				this.currentResults.modeCounterPrevious > 0 || this.currentResults.modeQrStaffPrevious > 0 || this.currentResults.modeQrOnlinePrevious > 0 || this.currentResults.modeDeliveryStaffPrevious > 0 || this.currentResults.modeDeliveryOnlinePrevious > 0 || this.currentResults.modeTakeawayStaffPrevious > 0 || this.currentResults.modeTakeawayOnlinePrevious > 0;
		},
		fiscalVisible() {
			return this.currentResults.fiscalPrinter > 0 || this.currentResults.apiscontrino > 0 || this.currentResults.apifattura > 0 ||
				this.currentResults.fiscalPrinterPrevious > 0 || this.currentResults.apiscontrinoPrevious > 0 || this.currentResults.apifatturaPrevious > 0;
		},
		terminalArray() {

			let terminals = {};

			this.ordersTerminals.forEach(order => {
				if (order.terminal_id) {
					if (!terminals.hasOwnProperty(order.terminal_id)) {
						terminals[order.terminal_id] = {
							name: order.terminal_name || "POS "+order.terminal_id,
							totalFormatted: 0,
							terminal_id: order.terminal_id,
							transactions: 0,
							prevTotalFormatted: 0,
							refunds: 0,
							refundsTotal: 0,
							prevTransactions: 0,
							increment: null
						};
					}

					if(order.date >= this.startDate && order.date < this.endDate) {
						terminals[order.terminal_id].totalFormatted += order.totalFormatted;
						terminals[order.terminal_id].transactions++;
						if(order.refundAmount > 0) {
							terminals[order.terminal_id].refunds++;
							terminals[order.terminal_id].refundsTotal += order.refundAmount;
						}
					} else if(order.date >= this.previousStartDate && order.date < this.startDate) {
						terminals[order.terminal_id].prevTotalFormatted += order.totalFormatted;
						terminals[order.terminal_id].prevTransactions++;
					}
				}
			});

			terminals = Object.values(terminals);

			terminals.forEach(terminal => {
				terminal.increment = this.terminalIncrement(terminal);
			});
			return terminals;
		},
		filterStationActive() {
			return this.userStore.businessUsers && (Object.keys(this.stationFilterValue.listOfUserToFilterBy).length !== Object.keys(this.userStore.businessUsers).length || Object.values(this.stationFilterValue.listOfUserToFilterBy).some(v => !v) || !this.stationFilterValue.onlineClient || !this.stationFilterValue.userToFilterBy);
		}
	},
	methods: {
		shouldMultiPaymentBeFilteredOut(payment) {
			if(payment.type !== "manual") {
				return false;
			}

			if(!this.userStore.manualCode) {
				return true;
			}

			// Check if payment date is before cutoff date
			let cutoffDate = this.userStore.currBusiness.counter_manual_cutoff_date;
			if(cutoffDate) {
				return payment.date < cutoffDate;
			}

			return false;
		},
		changeTimeSlot(newTimeSlot) {
			this.selectedTimeSlot = newTimeSlot;
		},
		terminalIncrement(terminal) {
			if(!terminal.prevTotalFormatted) { return null; }
			let difference = terminal.totalFormatted - terminal.prevTotalFormatted;
			let increment = difference/terminal.prevTotalFormatted*100;

			let finalValueRounded = Math.round(increment * 10) / 10;
			finalValueRounded = finalValueRounded % 1 ? finalValueRounded.toFixed(1) : finalValueRounded.toString();
			let stringResult = finalValueRounded + '%';

			if(stringResult.length > 6) {
				let returning = stringResult.substring(0, 3);
				returning += returning.endsWith('.') ? '..' : '...';
				return returning + '%';
			}

			return stringResult;

		},
		closeBottomSheet(active) {
			if(!active) {
				this.posDetails = false;
			}
		},
		closeBottomSheetBreakdown(active) {
			if(!active) {
				this.paymentsBreakdown = null;
			}
		},
		closeBottomSheetStations(active) {
			if(!active) {
				this.stationsFilter = false;
			}
		},
		getLastShiftDatetime(current) {
			let splitShift = this.dayShift.split(':');
			let dayShift = new Date(current);
			dayShift.setHours(splitShift[0] || 0, splitShift[1] || 0, splitShift[2] || 0, 0);
			return dayShift;
		},
		getReportData() {
			this.loadingResults = true;
			API.init().analyticsOrders(this.userStore.currBusinessId, formatDateTimeWithSeconds(this.previousStartDate), formatDateTimeWithSeconds(this.endDate), this.userStore.manualCode, true, response => {
				this.loadingResults = false;
				if (response && response.result === "OK") {
					(async () => {
						this.orders = this.analyticsStore.normalizeOrders(response.orders, response.orders_items, null, null, null, null, null, null, null, response.transactions, response.transactions_pos, response.payments, response.plugins, null, response.cancel_receipts);
						this.ordersTerminals = this.analyticsStore.normalizeOrdersTerminals(response.orders_terminals, response.transactions_pos, response.terminals_names);
					})()
				} else if(response && response.result === "KO" && response.details === "auth_denied") {
					this.notificationStore.showNotification("auth-denied-error");
					this.orders = [];
				}
			});
		},
		getCsvData() {
			// The order of the keys are vital on how the report get printed from the printer.
			let rows = {
				"total": {
					"total": this.currentResults.total,
					"cards": this.currentResults.totalCards,
					"cash": this.currentResults.totalCash,
					"other": this.currentResults.totalOther,
					"covers": this.currentResults.coverNum,
				},
				"fiscalPrinter": {
					"total": this.currentResults.fiscalPrinter,
					"cards": this.currentResults.fiscalPrinterCards,
					"cash": this.currentResults.fiscalPrinterCash,
					"other": this.currentResults.fiscalPrinterOther
				},
				"apiscontrino": {
					"total": this.currentResults.apiscontrino,
					"cards": this.currentResults.apiscontrinoCards,
					"cash": this.currentResults.apiscontrinoCash,
					"other": this.currentResults.apiscontrinoOther
				},
				"apifattura": {
					"total": this.currentResults.apifattura,
					"cards": this.currentResults.apifatturaCards,
					"cash": this.currentResults.apifatturaCash,
					"other": this.currentResults.apifatturaOther
				},
				"modeCounter": {
					"total": this.currentResults.modeCounter,
					"cards": this.currentResults.modeCounterCards,
					"cash": this.currentResults.modeCounterCash,
					"other": this.currentResults.modeCounterOther
				},
				"modeQrStaff": {
					"total": this.currentResults.modeQrStaff,
					"cards": this.currentResults.modeQrStaffCards,
					"cash": this.currentResults.modeQrStaffCash,
					"other": this.currentResults.modeQrStaffOther
				},
				"modeQrOnline": {
					"total": this.currentResults.modeQrOnline,
					"cards": this.currentResults.modeQrOnlineCards,
					"cash": this.currentResults.modeQrOnlineCash,
					"other": this.currentResults.modeQrOnlineOther
				},
				"modeDeliveryStaff": {
					"total": this.currentResults.modeDeliveryStaff,
					"cards": this.currentResults.modeDeliveryStaffCards,
					"cash": this.currentResults.modeDeliveryStaffCash,
					"other": this.currentResults.modeDeliveryStaffOther
				},
				"modeDeliveryOnline": {
					"total": this.currentResults.modeDeliveryOnline,
					"cards": this.currentResults.modeDeliveryOnlineCards,
					"cash": this.currentResults.modeDeliveryOnlineCash,
					"other": this.currentResults.modeDeliveryOnlineOther
				},
				"modeTakeawayStaff": {
					"total": this.currentResults.modeTakeawayStaff,
					"cards": this.currentResults.modeTakeawayStaffCards,
					"cash": this.currentResults.modeTakeawayStaffCash,
					"other": this.currentResults.modeTakeawayStaffOther
				},
				"modeTakeawayOnline": {
					"total": this.currentResults.modeTakeawayOnline,
					"cards": this.currentResults.modeTakeawayOnlineCards,
					"cash": this.currentResults.modeTakeawayOnlineCash,
					"other": this.currentResults.modeTakeawayOnlineOther
				}
			};


			// Simplified payment type structure - just the totals without unnecessary sub-categories
			const breakdown = this.currentResults.totalBreakdown;
			const paymentTypesInOrder = {};

			// Ensure stripe and cash come first in the order
			if (breakdown.hasOwnProperty('stripe')) {
				paymentTypesInOrder['stripe'] = breakdown['stripe'];
			}
			if (breakdown.hasOwnProperty('cash')) {
				paymentTypesInOrder['cash'] = breakdown['cash'];
			}

			// Add remaining payment types
			for (const typeKey in breakdown) {
				if (!paymentTypesInOrder.hasOwnProperty(typeKey)) {
					paymentTypesInOrder[typeKey] = breakdown[typeKey];
				}
			}

			// Create simplified payment type structure
			for (const typeKey in paymentTypesInOrder) {
				const totalValue = paymentTypesInOrder[typeKey];
				
				// Skip manual payments if not configured
				if(typeKey === "manual" && !this.userStore.manualCode) {
					continue;
				}

				// Use simplified structure: just the payment type and its total
				let typeKeyFormatted = "paymentType" + typeKey.substring(0, 1).toUpperCase() + typeKey.substring(1);
				rows[typeKeyFormatted] = totalValue;
			}

			return {rows};
		},
		exportTypePopup() {
			this.$refs['export-popup'].toggleStatus(true);
		},
		exportReport(expExtension) {

			const csvData = this.getCsvData();
			let dataXlsx = [];
			let paymentTypes = [];
			
			// Process each row and separate payment types
			Object.entries(csvData.rows).forEach(([key, value]) => {
				// Collect payment type entries separately
				if (key.startsWith('paymentType')) {
					const paymentTypeName = key.substring(11); // Remove 'paymentType' prefix
					paymentTypes.push({
						" ": this.getString(key) || paymentTypeName,
						"Total": value,
						"Electronic": "-",
						"Cash": "-", 
						"Other": "-"
					});
				} 
				// Handle regular entries with breakdown (total, modes, fiscal types)
				else if (typeof value === 'object' && value !== null) {
					dataXlsx.push({
						" ": this.getString(key),
						"Total": value.total || 0,
						"Electronic": value.cards || 0,
						"Cash": value.cash || 0,
						"Other": value.other || 0
					});
				}
			});

			// Insert section headers and empty rows
			dataXlsx.splice(1, 0, { " ": "", "Total": "", "Electronic": "", "Cash": "", "Other": "" });
			dataXlsx.splice(2, 0, { " ": this.getString('fiscalization_summary'), "Total": "", "Electronic": "", "Cash": "", "Other": "" });
			dataXlsx.splice(6, 0, { " ": "", "Total": "", "Electronic": "", "Cash": "", "Other": "" });
			dataXlsx.splice(7, 0, { " ": this.getString('modes_summary'), "Total": "", "Electronic": "", "Cash": "", "Other": "" });
			
			// Add payment types section at the end if there are any
			if (paymentTypes.length > 0) {
				dataXlsx.push({ " ": "", "Total": "", "Electronic": "", "Cash": "", "Other": "" });
				dataXlsx.push({ " ": this.getString('payment_types') || "Payment Types", "Total": "", "Electronic": "", "Cash": "", "Other": "" });
				dataXlsx.push(...paymentTypes);
			}

			let date = formatDateOnly(this.date || new Date());
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm+"_"+this.getString("report").toLowerCase()+"_"+date;

			if(dataXlsx.length > 0){
				this.scriptStore.loadScript('xlsx', () => {
					const worksheet = window.XLSX.utils.json_to_sheet(dataXlsx);
					const workbook = window.XLSX.utils.book_new();
					window.XLSX.utils.book_append_sheet(workbook, worksheet, date);

					window.XLSX.utils.sheet_add_aoa(worksheet, [[" ", "Total", "Electronic", "Cash", "Other"]], {origin: "A1"});

					if (expExtension === "xlsx") {
						window.XLSX.writeFileXLSX(workbook, fileName + ".xlsx");
					} else {
						window.XLSX.writeFile(workbook, fileName + ".csv");
					}
				});
			}
			else {
				this.notificationStore.showNotification("table-no-data");
			}
		},
		sendPrintReportCommand() {
			this.printReportCloudPrinter();
		},
		printReportCloudPrinter() {

			const csvData = this.getCsvData();

			let appliedFilters = null;
			if(this.filterStationActive) {
				for(let key in this.stationFilterValue.listOfUserToFilterBy) {
					if(this.stationFilterValue.listOfUserToFilterBy[key]) {
						if(!appliedFilters) {
							appliedFilters = [];
						}
						//We need to get the name of the user
						let user = this.userStore.businessUsers[key];
						if(user) {
							appliedFilters.push(user.name);
						} else {
							appliedFilters.push(key);
						}
					}
				}
				if(this.stationFilterValue.onlineClient) {
					if(!appliedFilters) {
						appliedFilters = [];
					}
					appliedFilters.push("online");
				}
			}
			if(appliedFilters) {
				appliedFilters = JSON.stringify(appliedFilters);
			}

			this.loadingPrinter = true;
			let reportTitle = formatDateOnly(this.date || new Date());
			let reportSubtitle = null;
			if(this.selectedTimeSlot) {
				reportSubtitle = this.selectedTimeSlot?.name;
				reportSubtitle += " (" + this.selectedTimeSlot.hour_start + ":" + this.selectedTimeSlot.minute_start;
				reportSubtitle += " - ";
				reportSubtitle += this.selectedTimeSlot.hour_end + ":" + this.selectedTimeSlot.minute_end+")";
			}
			API.init().printReport(this.userStore.currBusinessId, JSON.stringify(csvData.rows), reportTitle, reportSubtitle, appliedFilters, response => {
				this.loadingPrinter = false;
				if (response && response.result === "OK") {
					this.notificationStore.showNotification("print-success");
				} else if(response?.details === 'no_printers_found') {
					this.notificationStore.showNotification("no-printers-found");
				} else {
					this.notificationStore.showNotification("generic-error");
				}
			});

		},
		applyExecutedByFilter(value) {
			this.stationFilterValue = value;
			this.stationsFilter = false;
		},
	},
	mounted() {
		this.getReportData();
		this.printersStore.getBusinessPrinters();
	},
	watch: {
		date() {
			this.getReportData();
		},
		selectedTimeSlot() {
			this.getReportData();
		},
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.selectedTimeSlot = null;
				this.printersStore.getBusinessPrinters();
				this.getReportData();
			}
		}
	}
}
</script>


<style scoped lang="scss">
.qromo-report-container {
	padding: 6px;

	.date-picker-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		background-color: var(--color-bg-primary-2);
		padding: 10px 12px;
		border-radius: 13px;
		box-shadow: inset 0 1px 2px var(--color-alpha-4);
		gap: 10px;
		flex-wrap: wrap;


		.date-container {
			display: flex;
			align-items: center;
			gap: 10px;
			min-width: 200px;
		}

		.title {
			font-size: 18px;
			font-weight: 600;
			color: $txt_0;
		}

		.input-wrapper {
			width: 150px;
		}

		.buttons-container {
			display: flex;
			gap: 8px;
			margin-left: auto;
		}

	}

	.container-mini-cards-total {
		display: flex;
		justify-content: space-between;
		flex-wrap: wrap;
		--gap-size: 8px;
		gap: var(--gap-size);

		.container-mini-card {
			width: calc(25% - var(--gap-size));
			border: 1px solid transparent;

			.dark-blue {
				background-color: var(--color-bg-primary-2);
				border: 1px solid var(--color-primary);
				cursor: pointer;
			}

			@media (max-width: 1439px) {
				width: calc(50% - var(--gap-size));
			}

			@media (max-width: 500px) {
				width: 100%;
			}
		}
	}

	.container-mini-cards-category {
		display: flex;
		justify-content: space-between;
		flex-wrap: wrap;
		--gap-size: 8px;
		gap: var(--gap-size);

		.container-mini-card {
			width: calc((100% / 3) - var(--gap-size));

			.dark-blue {
				background-color: var(--color-bg-primary-2);
			}

			@media (max-width: 767px) {
				width: calc(50% - var(--gap-size));
			}

			@media (max-width: 440px) {
				width: 100%;
			}
		}
	}

	.container-header {
		width: 100%;

		h3 {
			font-size: 18px;
			font-weight: 600;
			color: $txt_0;
			@include truncateString(1);
			padding-right: 5px;
		}

		.container-mode-chooser {
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			width: 100%;
			margin-top: 8px;
			--num-modes: 1;

			.mode {
				border: 1px solid var(--color-primary);
				font-size: 14px;
				color: var(--color-primary);
				background-color: var(--color-bg);
				padding: 6px 8px;
				transition: background-color 0.2s ease;
				width: calc(100% / var(--num-modes));
				border-right: none;
				cursor: pointer;
				@include truncateString(1);

				&.active {
					background-color: var(--color-active-button);
					color: var(--color-active-button-text);
				}

				&:first-child {
					border-top-left-radius: 7px;
					border-bottom-left-radius: 7px;
				}

				&:last-child {
					border-right: 1px solid var(--color-primary);
					border-top-right-radius: 7px;
					border-bottom-right-radius: 7px;
				}

				@media (max-width: 500px) {
					width: 100%;
					border-right: 1px solid var(--color-primary);
					border-bottom: none;

					&:first-child {
						border-top-left-radius: 7px;
						border-top-right-radius: 7px;
						border-bottom-left-radius: 0;
					}

					&:last-child {
						border-bottom: 1px solid var(--color-primary);
						border-top-right-radius: 0;
						border-bottom-left-radius: 7px;
					}

				}

			}

		}
	}

}
.terminals-container {
	display: flex;
	flex-direction: column;
	gap: 10px;

	.terminal-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 10px;
		background-color: var(--color-alpha-1);
		border-radius: 13px;
		gap: 10px;

		.terminal-header {
			display: flex;
			flex-direction: column;
			gap: 4px;

			.terminal-name {
				font-size: 16px;
				font-weight: 500;
			}

			.transactions, .refund-part {
				font-size: 14px;
				color: var(--color-txt-1);
			}

		}


		.terminal-data {
			display: flex;
			gap: 4px;
			flex-direction: column;
			align-items: flex-end;

			.increment {
				font-size: 12px;
				line-height: 14px;
				user-select: none;
				@include truncateString(1);

				border-radius: 8px;
				padding: 4px 5px;
				max-width: 55px;
				background: rgb(215,247,194);
				color: rgb(0,105,8);

				&.negative {
					background: rgb(252,237,185);
					color: rgb(168,44,0);
				}
			}

			.value {
				font-size: 18px;
				font-weight: 700;
			}
		}
	}
}
</style>